import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';
import {
  Asset,
  IAssetIpHistory,
  IAssetRemediation,
  IAssetsDomainDetails,
  IAssetsGraph,
  IAssetsIpAddressDetails,
  IAssetsPortDetails,
  IAssetVulnerabilities,
} from '../../../core/store/digital-footprint/assets/models/Asset';
import {
  FilterProps,
  PagedResponse,
} from '../../../core/store/general/models/general';
import { createParams } from '../../../core/store/utils/utils';

@Injectable({
  providedIn: 'root',
})
export class AssetsService {
  private baseApiUrl: string = environment.API_BASE_URL;

  constructor(private readonly http: HttpClient) {}

  getAssets(props: FilterProps): Observable<PagedResponse<Asset>> {
    return this.http.get<PagedResponse<Asset>>(
      `${this.baseApiUrl}/digital-footprint/assets${createParams(props)}`,
    );
  }

  getAssetVulnerabilities(
    assetId: string,
  ): Observable<PagedResponse<IAssetVulnerabilities>> {
    return this.http.get<PagedResponse<IAssetVulnerabilities>>(
      `${this.baseApiUrl}/digital-footprint/assets/${assetId}/vulnerabilities`,
    );
  }

  getAssetsGraph(): Observable<IAssetsGraph> {
    return this.http.get<IAssetsGraph>(
      `${this.baseApiUrl}/digital-footprint/assets/graph`,
    );
  }

  getAssetsGraphDomainSubdomainOverview(
    id: string,
  ): Observable<IAssetsDomainDetails> {
    return this.http.get<IAssetsDomainDetails>(
      `${this.baseApiUrl}/digital-footprint/assets/${id}/overview`,
    );
  }

  getAssetsGraphOpenPortOverview(id: string): Observable<IAssetsPortDetails> {
    return this.http.get<IAssetsPortDetails>(
      `${this.baseApiUrl}/digital-footprint/open-ports/${id}/overview`,
    );
  }

  getAssetsIpAddressOverview(ip: string): Observable<IAssetsIpAddressDetails> {
    return this.http.get<IAssetsIpAddressDetails>(
      `${this.baseApiUrl}/digital-footprint/ips/${ip}/overview`,
    );
  }

  // ---- asset detail page ----
  // The overview endpoint is shared with the graph sidebar; the page fetches it under its
  // own action so the graph's detail-clearing cannot wipe page state.
  getAssetOverview(id: string): Observable<IAssetsDomainDetails> {
    return this.http.get<IAssetsDomainDetails>(
      `${this.baseApiUrl}/digital-footprint/assets/${id}/overview`,
    );
  }

  // Same list endpoint the assets page uses, scoped to one parent. Deliberately kept out
  // of the shared assets slice: the list page owns that state, and a subdomain tab writing
  // into it would empty the list behind the user.
  getAssetSubdomains(parentAssetId: string): Observable<PagedResponse<Asset>> {
    return this.http.get<PagedResponse<Asset>>(
      `${this.baseApiUrl}/digital-footprint/assets?parentAssetId=${parentAssetId}&pageSize=20`,
    );
  }

  getAssetIpHistory(id: string): Observable<IAssetIpHistory> {
    return this.http.get<IAssetIpHistory>(
      `${this.baseApiUrl}/digital-footprint/assets/${id}/ip-history`,
    );
  }


  getAssetRemediation(id: string): Observable<IAssetRemediation> {
    return this.http.get<IAssetRemediation>(
      `${this.baseApiUrl}/digital-footprint/assets/${id}/remediation`,
    );
  }

  setPrimaryDomain(newPrimaryDomainAssetId: string): Observable<unknown> {
    return this.http.post(
      `${this.baseApiUrl}/digital-footprint/assets/set-primary-domain`,
      { newPrimaryDomainAssetId },
    );
  }
}
